import * as amplitude from '@amplitude/analytics-browser';
import Cookies, {
  type CookieGetOptions,
  type CookieSetOptions,
} from 'universal-cookie';

import { isDev } from '~/dev/utils/isDev';
import { isProd } from '~/dev/utils/isProd';
import { StorageService } from '~/services/StorageService/StorageService';
import { getWildDomain } from '~/utils/browser';
import { testIsValidWindowOpener } from '~/utils/dom';

// TODO: DEPRECATE Next 2 lines - EKP-15824
const access_token = 'access_token';
const refresh_token = 'refresh_token';

// Cookies
const LOGGED_IN_COOKIE = 'logged_in_cookie';
const EXPIRES_AT_COOKIE = 'expires_at_cookie';
const IDLE_TIMEOUT_COOKIE = 'idle_timeout_cookie';
// Local Storage
const inactive_expires_at = 'inactive_expires_at';
const access_token_created_at = 'access_token_created_at';
const session_subdomain = 'session_subdomain';
const IS_WORKDAY_USER_KEY = 'is_workday_user';
const REQUIRES_REAUTH_KEY = 'requires_reauth';

type ClearAuthenticationOptions = {
  /** When true, set the reauth flag so the modal handles re-auth instead of a hard redirect. */
  requiresReauth?: boolean;
};
// Session Storage
const zendesk_redirect_url = 'zendesk_redirect_url';
const zendesk_redirect_expire = 'zendesk_redirect_expire';
// Aegis OAuth proxy: UI stores individual OAuth params (client_id, redirect_uri, state)
// and forwards them to aegis /ext/oauth/redirect, which issues an authorization code.
// Currently used for the legacy Workramp/Academy integration.
const aegis_oauth_proxy_redirect_uri = 'aegis_oauth_proxy_redirect_uri';
const aegis_oauth_proxy_client_id = 'aegis_oauth_proxy_client_id';
const aegis_oauth_proxy_state = 'aegis_oauth_proxy_state';
const aegis_oauth_proxy_expire = 'aegis_oauth_proxy_expire';
// Rolodex OAuth bounce: when a user hits rolodex /oauth/authorize without a session,
// rolodex redirects to the UI with ?return_url={authorize_url}. After login, the UI
// navigates back to that URL so rolodex can complete the authorization code flow.
// Distinct from return_to, which is the Zendesk SSO redirect target (guarded by sso=zendesk).
const rolodex_oauth_bounce_url = 'rolodex_oauth_bounce_url';
const rolodex_oauth_bounce_expire = 'rolodex_oauth_bounce_expire';

const TRUSTED_REDIRECT_HOST_REGEX = /^(?:[a-z0-9-.]+)*\.evisort\.(com|dev)$/i;
const trustedRolodexPathPrefixes = ['/api/v1/oidc/', '/api/v1/oauth/authorize'];

// Module-scoped mutable holder for the CSRF token. The previous implementation
// used `React.createRef`, but `RefObject.current` is readonly in TS and the
// React dependency was only there to mimic this shape.
const CSRF_TOKEN_REFERENCE: { current: string | null } = { current: null };

// Module-scoped (not persisted) rather than component state: set while
// `AuthSessionBootstrap` has a `CrossClientSwitchConfirmModal` awaiting a
// decision, so `AuthRequireWarningModal` — a sibling, not a child — can
// suppress its own subdomain-mismatch popup for that same window instead of
// both modals racing to show at once. Deliberately not persisted to
// localStorage/sessionStorage: it should reset to false on every fresh page
// load, the same as the confirmation itself.
let pendingClientSwitchConfirmation = false;

export interface AuthenticationStoreSnapshot {
  isLoggedIn: boolean;
  isAuthSessionExpired: boolean;
  isInactiveTimeoutReached: boolean;
  requiresReauth: boolean;
  isEmbed: boolean;
  isAegisAuthed: boolean;
  isRolodexAuthed: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  csrf: string | null;
  subDomainInSession: string | null;
  isWorkdayUser: string | null;
  hasPendingClientSwitchConfirmation: boolean;
}

// Reactive layer: pub/sub used by `useAuthenticationStore` so React components
// can re-render when auth-relevant cookies / storage values change. Cookies
// don't emit native events, so mutator methods below explicitly call
// `notifyAuthChange()` after they write.
type AuthChangeListener = () => void;

const authStoreListeners = new Set<AuthChangeListener>();
let cachedSnapshot: AuthenticationStoreSnapshot | null = null;

const shallowEqualSnapshot = (
  a: AuthenticationStoreSnapshot | null,
  b: AuthenticationStoreSnapshot | null,
) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = Object.keys(a) as (keyof AuthenticationStoreSnapshot)[];
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => a[k] === b[k]);
};

const computeAuthSnapshot = () => {
  const store = AuthenticationStore();
  return {
    isLoggedIn: Boolean(store.isLoggedIn()),
    isAuthSessionExpired: Boolean(store.isAuthSessionExpired()),
    isInactiveTimeoutReached: Boolean(store.isInactiveTimeoutReached()),
    requiresReauth: Boolean(store.requiresReauth()),
    isEmbed: Boolean(store.isEmbed()),
    isAegisAuthed: Boolean(store.isAegisAuthed()),
    isRolodexAuthed: Boolean(store.isRolodexAuthed()),
    accessToken: store.getAccessToken() ?? null,
    refreshToken: store.getRefreshToken() ?? null,
    csrf: store.getCsrf() ?? null,
    subDomainInSession: store.getSubDomainInSession() ?? null,
    isWorkdayUser: store.getIsWorkdayUser() ?? null,
    hasPendingClientSwitchConfirmation:
      store.hasPendingClientSwitchConfirmation(),
  };
};

export const getAuthSnapshot = (): AuthenticationStoreSnapshot => {
  if (cachedSnapshot === null) {
    cachedSnapshot = computeAuthSnapshot();
  }
  return cachedSnapshot;
};

export const subscribeToAuthChanges = (listener: AuthChangeListener) => {
  authStoreListeners.add(listener);
  return () => {
    authStoreListeners.delete(listener);
  };
};

// Composite mutations (`clearAuthentication`, `saveAuthentication`) call
// several smaller mutators that each end with `notifyAuthChange()`, which on
// its own would emit 3+ notifications per logout — each recomputing the
// snapshot and (without React's `useSyncExternalStore` batching saving us)
// momentarily exposing intermediate states like "csrf still set, but
// isWorkdayUser already cleared" to non-React subscribers.
//
// Wrap composite ops with `batchAuthMutations` so inner notifications are
// coalesced into one fire-on-unwind. Nested batches (e.g. saveAuthentication
// calling clearAuthentication) compose naturally via the depth counter.
let authBatchDepth = 0;
let authBatchPending = false;

const batchAuthMutations = (fn: () => void) => {
  authBatchDepth += 1;
  try {
    fn();
  } finally {
    authBatchDepth -= 1;
    if (authBatchDepth === 0 && authBatchPending) {
      authBatchPending = false;
      notifyAuthChange();
    }
  }
};

const notifyAuthChange = () => {
  if (authBatchDepth > 0) {
    authBatchPending = true;
    return;
  }
  let next: AuthenticationStoreSnapshot;
  try {
    next = computeAuthSnapshot();
  } catch {
    // If a getter throws (e.g., during teardown / weird mock state), keep
    // serving the previous snapshot rather than poisoning every mutator.
    return;
  }
  // Always reschedule: a mutator may have rewritten an expiry timestamp without
  // changing any boolean field (e.g. saveAuthentication writes a new
  // expires_at_cookie further in the future), and we still need the timer to
  // re-target.
  scheduleNextExpiryTick();

  // When the session clock-expires while the user had an active session,
  // auto-set the reauth flag so PrivateRoute shows the modal instead of
  // hard-redirecting to /login. We detect the exact transition
  // (was logged in, not yet expired → now expired, no manual reauth flag)
  // so a deliberate logout followed by a stale expiry tick doesn't re-arm.
  if (
    cachedSnapshot?.isLoggedIn &&
    !cachedSnapshot.isAuthSessionExpired &&
    next.isAuthSessionExpired &&
    !next.requiresReauth
  ) {
    sessionStorage.setItem(REQUIRES_REAUTH_KEY, 'true');
    next = { ...next, requiresReauth: true };
  }

  if (cachedSnapshot && shallowEqualSnapshot(cachedSnapshot, next)) return;
  cachedSnapshot = next;
  authStoreListeners.forEach((listener) => listener());
};

// Time-based expiry scheduler.
//
// `isAuthSessionExpired` and `isInactiveTimeoutReached` flip from `false` to
// `true` purely from the wall clock crossing a stored timestamp, with no
// corresponding cookie / storage write. Without this scheduler, the cached
// snapshot would keep reporting `isAuthSessionExpired: false` indefinitely
// after the EXPIRES_AT_COOKIE timestamp passes, until something else triggers
// `notifyAuthChange()` (a tab focus, a mutator, …).
//
// The scheduler reads the raw timestamps directly (rather than going through
// `AuthenticationStore()`) so it can run during module init before any
// consumer has called the factory, and so it has no dependency on the
// `getWildDomain` / cookie-options plumbing inside the factory.
let nextExpiryTimer: ReturnType<typeof setTimeout> | null = null;
let nextExpiryTimerAt: number | null = null;

const computeNextExpiryAtMs = () => {
  if (typeof window === 'undefined') return null;
  if (isDev) return null;

  let next: number | null = null;

  // EXPIRES_AT_COOKIE stores seconds since epoch. `isAuthSessionExpired` flips
  // when `Math.ceil(Date.now() / 1000) > expiresAt`, i.e. when the wall clock
  // crosses (expiresAt + 1) seconds. We aim for that boundary in ms.
  try {
    const cookies = new Cookies();
    const expiresAtSec = cookies.get<string | undefined>(EXPIRES_AT_COOKIE);
    if (expiresAtSec) {
      const expiresAtMs = (parseInt(expiresAtSec, 10) + 1) * 1000;
      if (Number.isFinite(expiresAtMs) && expiresAtMs > Date.now()) {
        next = expiresAtMs;
      }
    }
  } catch {
    // Cookie parsing failures are non-fatal — the snapshot still works.
  }

  // inactive_expires_at stores ms since epoch. `isInactiveTimeoutReached`
  // flips when `Date.now() > inactiveExpiresAt`.
  try {
    const inactiveExpiresAt = StorageService.getItem(inactive_expires_at);
    if (inactiveExpiresAt) {
      const inactiveExpiresAtMs = parseInt(inactiveExpiresAt, 10);
      if (
        Number.isFinite(inactiveExpiresAtMs) &&
        inactiveExpiresAtMs > Date.now()
      ) {
        next =
          next === null
            ? inactiveExpiresAtMs
            : Math.min(next, inactiveExpiresAtMs);
      }
    }
  } catch {
    // StorageService failures are non-fatal.
  }

  return next;
};

const scheduleNextExpiryTick = () => {
  if (typeof window === 'undefined') return;

  const target = computeNextExpiryAtMs();
  if (target === null) {
    if (nextExpiryTimer !== null) {
      clearTimeout(nextExpiryTimer);
      nextExpiryTimer = null;
      nextExpiryTimerAt = null;
    }
    return;
  }

  // Already aiming at the same boundary — no point re-arming.
  if (nextExpiryTimer !== null && nextExpiryTimerAt === target) return;

  if (nextExpiryTimer !== null) {
    clearTimeout(nextExpiryTimer);
  }
  // Pad by 250ms so `Date.now()` reliably lands past the boundary by the time
  // the comparison runs (timers fire on or after the requested delay).
  const delay = Math.max(0, target - Date.now()) + 250;
  nextExpiryTimerAt = target;
  nextExpiryTimer = setTimeout(() => {
    nextExpiryTimer = null;
    nextExpiryTimerAt = null;
    notifyAuthChange();
  }, delay);
};

if (typeof window !== 'undefined') {
  // Cross-tab updates: any write to localStorage from another tab fires here.
  // Cookies still aren't covered (no API for it), so cross-tab cookie writes
  // are picked up on the next visibilitychange instead.
  window.addEventListener('storage', notifyAuthChange);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') notifyAuthChange();
  });
  // Bootstrap a timer for whatever expiry timestamps are already on disk
  // (e.g. when the tab was closed mid-session and reopened).
  scheduleNextExpiryTick();
}

const isTrustedRedirectHost = (host: string) => {
  if (isDev) return true;
  if (host.toLowerCase() === window.location.hostname.toLowerCase())
    return true;
  return TRUSTED_REDIRECT_HOST_REGEX.test(host);
};

const sanitizeRolodexOAuthBounceUrl = (value: string | null) => {
  const trimmedValue = value?.trim();

  if (!trimmedValue || typeof window === 'undefined') return null;

  try {
    // Parsed without a base: rolodex always sends an absolute URL, and
    // resolving relative values against window.location.origin would let
    // them pass the same-origin branch of isTrustedRedirectHost below.
    const parsedUrl = new URL(trimmedValue);
    const protocol = parsedUrl.protocol.toLowerCase();
    const isTrustedPath = trustedRolodexPathPrefixes.some((prefix) =>
      parsedUrl.pathname.startsWith(prefix),
    );
    const isHttpScheme =
      protocol === 'https:' || (isDev && protocol === 'http:');

    if (
      !isHttpScheme ||
      !isTrustedPath ||
      parsedUrl.username ||
      parsedUrl.password ||
      !isTrustedRedirectHost(parsedUrl.hostname)
    )
      return null;

    return parsedUrl.toString();
  } catch {
    return null;
  }
};

const AuthenticationStore = () => {
  const defaultOptions = {
    domain: getWildDomain(window.location.hostname),
    path: '/',
    // Set to None for embedded sessions. For local dev,
    // we set to Lax to allow cookies to be set.
    sameSite: isDev ? 'Lax' : 'None',
    // Secure is set to false for local dev to allow cookies to be set.
    secure: !isDev,
    // `universal-cookie` types want lowercase sameSite ('lax' | 'none') but the
    // app has shipped 'Lax' / 'None' for years and the underlying cookie parser
    // is case-insensitive. Cast rather than change the runtime value.
  } as unknown as CookieSetOptions;

  // `cookie.get` / `cookie.remove` accept a different options shape that only
  // covers parsing flags. We reuse `defaultOptions` at the call site and cast
  // because passing the extra fields is harmless at runtime.
  const getOptions = defaultOptions as unknown as CookieGetOptions;

  const constants = {
    MS_UNTIL_ZENDESK_EXPIRE: 5 * 60 * 1000,
    MS_UNTIL_OAUTH_EXPIRE: 5 * 60 * 1000,
    S_UNTIL_INACTIVE_TIMEOUT: 3600,
  };

  const cookie = new Cookies();

  // we consider the session embedded (e.g. in an iframe) if the window is not the current/top window, or if it has been opened by another window.
  function isEmbed() {
    if (isProd) {
      return window.self !== window.top || testIsValidWindowOpener();
    }
    return false;
  }

  function getStoredLoggedInCookie() {
    return cookie.get(LOGGED_IN_COOKIE, getOptions);
  }

  function getStoredExpiresAtCookie() {
    return cookie.get<string | undefined>(EXPIRES_AT_COOKIE, getOptions);
  }

  function getUserConfigInactivityTimeout() {
    const timeout = cookie.get<string | undefined>(
      IDLE_TIMEOUT_COOKIE,
      getOptions,
    );

    return timeout ? parseInt(timeout, 10) : undefined;
  }

  // Priority: user config > client config > default constant
  function getInactiveTimeoutInSeconds(clientConfigTimeout?: number | null) {
    const userConfigTimeout = getUserConfigInactivityTimeout();

    if (userConfigTimeout && userConfigTimeout > 0) {
      return userConfigTimeout;
    }

    return clientConfigTimeout && clientConfigTimeout > 0
      ? clientConfigTimeout
      : constants.S_UNTIL_INACTIVE_TIMEOUT;
  }

  function getIsWorkdayUser() {
    return StorageService.getItem(IS_WORKDAY_USER_KEY);
  }
  function setIsWorkdayUser(isWorkdayUser: boolean | string) {
    StorageService.setItem(IS_WORKDAY_USER_KEY, String(isWorkdayUser));
    notifyAuthChange();
  }
  function deleteIsWorkdayUser() {
    StorageService.removeItem(IS_WORKDAY_USER_KEY);
    notifyAuthChange();
  }

  function saveAccessToken(accessToken: string) {
    cookie.set(access_token, accessToken, defaultOptions);
    notifyAuthChange();
  }

  function removeInactiveTimer() {
    StorageService.removeItem(inactive_expires_at);
    notifyAuthChange();
  }

  function resetInactiveTimer(timeoutInSecond: number) {
    StorageService.setItem(
      inactive_expires_at,
      String(Date.now() + timeoutInSecond * 1000),
    );
    notifyAuthChange();
  }

  function saveAuthentication(accessToken: string, refreshToken: string) {
    batchAuthMutations(() => {
      clearAuthentication();
      // A previous session (possibly a different tenant, since this key is
      // localStorage-backed and outlives logout) can leave a stale subdomain
      // behind. Left in place, `AuthRequireWarningModal`'s mismatch check
      // compares it against the freshly logged-in URL and flashes "Sign in
      // Required" until `AuthSessionBootstrap` re-resolves and overwrites it
      // from the newly loaded user. Clearing it here closes that gap.
      StorageService.removeItem(session_subdomain);

      cookie.set(access_token, accessToken, defaultOptions);

      const maxAge = parseInt(getStoredExpiresAtCookie() ?? '', 10);
      cookie.set(refresh_token, refreshToken, {
        ...defaultOptions,
        maxAge:
          !Number.isNaN(maxAge) && !isDev
            ? maxAge - Math.ceil(Date.now() / 1000)
            : undefined,
      });

      removeInactiveTimer();
      saveJwtTokenCreationTime();
      notifyAuthChange();
    });
  }

  function clearAuthentication(options?: ClearAuthenticationOptions) {
    batchAuthMutations(() => {
      if (options?.requiresReauth) {
        sessionStorage.setItem(REQUIRES_REAUTH_KEY, 'true');
      } else {
        sessionStorage.removeItem(REQUIRES_REAUTH_KEY);
      }

      if (!isDev) amplitude.reset();

      StorageService.removeItem(inactive_expires_at);
      deleteIsWorkdayUser();
      // for non-httponly auth path
      cookie.remove(access_token, defaultOptions);
      cookie.remove(refresh_token, defaultOptions);
      // for httponly auth path
      cookie.remove(LOGGED_IN_COOKIE, defaultOptions);

      // set csrf to null so it can refetch
      setCsrf(null);
      notifyAuthChange();
    });
  }

  function getAccessToken() {
    return cookie.get<string | undefined>(access_token, getOptions);
  }

  function getRefreshToken() {
    return cookie.get<string | undefined>(refresh_token, getOptions);
  }

  function isAuthSessionExpired() {
    if (isDev) return false;
    const expiresAt = getStoredExpiresAtCookie();
    if (!expiresAt) return true;

    return Math.ceil(Date.now() / 1000) > parseInt(expiresAt, 10);
  }

  function isInactiveTimeoutReached() {
    const inactiveExpireTime = StorageService.getItem(inactive_expires_at);
    if (!inactiveExpireTime) return false;

    return Date.now() > parseInt(inactiveExpireTime, 10);
  }

  function shouldConsiderSessionValid() {
    if (getStoredExpiresAtCookie() && !isDev) {
      return !isAuthSessionExpired();
    }
    return true;
  }

  function shouldConsiderUserLoggedIn() {
    // for non-httponly auth path - TODO: DEPRECATE Next line - EKP-15824
    const hasValidTokens = Boolean(getAccessToken() && getRefreshToken());
    // for httponly auth path
    const hasLoggedInFlag = Boolean(getStoredLoggedInCookie());

    return hasValidTokens || hasLoggedInFlag;
  }

  function shouldConsiderSameCell() {
    if (isDev) return true;

    const cookieValue = getStoredLoggedInCookie();
    if (!cookieValue || typeof cookieValue !== 'string') return true;

    const dotIndex = cookieValue.indexOf('.');
    const cookieCell = dotIndex > -1 ? cookieValue.slice(dotIndex + 1) : '';
    const hostname = window.location.hostname;

    if (!cookieCell || !hostname.startsWith('clients.')) return true;

    return (
      cookieCell.toLowerCase() ===
      hostname.slice('clients.'.length).toLowerCase()
    );
  }

  function isRolodexAuthed() {
    const cookieValue = getStoredLoggedInCookie();
    return (
      typeof cookieValue === 'string' &&
      cookieValue.toUpperCase().includes('ROLODEX')
    );
  }

  function isAegisAuthed() {
    const cookieValue = getStoredLoggedInCookie();
    return (
      typeof cookieValue === 'string' &&
      cookieValue.toUpperCase().includes('AEGIS')
    );
  }

  function isLoggedIn() {
    return (
      shouldConsiderUserLoggedIn() &&
      shouldConsiderSessionValid() &&
      shouldConsiderSameCell()
    );
  }

  function requiresReauth() {
    return sessionStorage.getItem(REQUIRES_REAUTH_KEY) === 'true';
  }

  function markRequiresReauth() {
    sessionStorage.setItem(REQUIRES_REAUTH_KEY, 'true');
    notifyAuthChange();
  }

  function getSubDomainInSession() {
    return StorageService.getItem(session_subdomain);
  }

  function saveSubDomainInSession(subdomain: string) {
    StorageService.setItem(session_subdomain, subdomain);
    notifyAuthChange();
  }

  function hasPendingClientSwitchConfirmation() {
    return pendingClientSwitchConfirmation;
  }

  function setPendingClientSwitchConfirmation(pending: boolean) {
    pendingClientSwitchConfirmation = pending;
    notifyAuthChange();
  }

  function getZendeskUrl() {
    return sessionStorage.getItem(zendesk_redirect_url);
  }

  function getZendeskExpire() {
    return sessionStorage.getItem(zendesk_redirect_expire);
  }

  const setZendeskUrl = (redirectUrl: string) => {
    const zendeskRedirectExp = (
      new Date().valueOf() + constants.MS_UNTIL_ZENDESK_EXPIRE
    ).toString();

    sessionStorage.setItem(zendesk_redirect_url, redirectUrl);
    sessionStorage.setItem(zendesk_redirect_expire, zendeskRedirectExp);
  };

  const removeZendeskFlag = () => {
    sessionStorage.removeItem(zendesk_redirect_url);
    sessionStorage.removeItem(zendesk_redirect_expire);
  };

  function getAegisOAuthProxyExpire() {
    return sessionStorage.getItem(aegis_oauth_proxy_expire);
  }

  function getAegisOAuthProxyVars() {
    return [
      sessionStorage.getItem(aegis_oauth_proxy_redirect_uri),
      sessionStorage.getItem(aegis_oauth_proxy_client_id),
      sessionStorage.getItem(aegis_oauth_proxy_state),
    ] as [string | null, string | null, string | null];
  }

  const setAegisOAuthProxyVars = (
    redirectUri: string,
    clientId: string,
    state: string,
  ) => {
    const expireAt = (
      new Date().valueOf() + constants.MS_UNTIL_OAUTH_EXPIRE
    ).toString();

    sessionStorage.setItem(aegis_oauth_proxy_redirect_uri, redirectUri);
    sessionStorage.setItem(aegis_oauth_proxy_client_id, clientId);
    sessionStorage.setItem(aegis_oauth_proxy_state, state);
    sessionStorage.setItem(aegis_oauth_proxy_expire, expireAt);
  };

  const removeAegisOAuthProxy = () => {
    sessionStorage.removeItem(aegis_oauth_proxy_redirect_uri);
    sessionStorage.removeItem(aegis_oauth_proxy_client_id);
    sessionStorage.removeItem(aegis_oauth_proxy_state);
    sessionStorage.removeItem(aegis_oauth_proxy_expire);
  };

  function getRolodexOAuthBounceUrl() {
    const storedUrl = sessionStorage.getItem(rolodex_oauth_bounce_url);
    const sanitizedUrl = sanitizeRolodexOAuthBounceUrl(storedUrl);

    if (!sanitizedUrl) {
      removeRolodexOAuthBounce();
      return null;
    }

    return sanitizedUrl;
  }

  function getRolodexOAuthBounceExpire() {
    return sessionStorage.getItem(rolodex_oauth_bounce_expire);
  }

  const setRolodexOAuthBounceUrl = (url: string) => {
    const sanitizedUrl = sanitizeRolodexOAuthBounceUrl(url);
    if (!sanitizedUrl) {
      removeRolodexOAuthBounce();
      return;
    }

    const expireAt = (
      new Date().valueOf() + constants.MS_UNTIL_OAUTH_EXPIRE
    ).toString();

    sessionStorage.setItem(rolodex_oauth_bounce_url, sanitizedUrl);
    sessionStorage.setItem(rolodex_oauth_bounce_expire, expireAt);
  };

  const removeRolodexOAuthBounce = () => {
    sessionStorage.removeItem(rolodex_oauth_bounce_url);
    sessionStorage.removeItem(rolodex_oauth_bounce_expire);
  };

  const saveJwtTokenCreationTime = () => {
    StorageService.setItem(
      access_token_created_at,
      String(new Date().getTime()),
    );
    notifyAuthChange();
  };

  const getAccessTokenCreationTime = () => {
    const tokenCreationTime = StorageService.getItem(access_token_created_at);
    return tokenCreationTime ? parseInt(tokenCreationTime, 10) : null;
  };

  const setCsrf = (token: string | null) => {
    CSRF_TOKEN_REFERENCE.current = token;
    if (token) {
      StorageService.setEncryptionKey(token);
    }
    notifyAuthChange();
  };

  const getCsrf = () => {
    return CSRF_TOKEN_REFERENCE.current;
  };

  const getCacheEncryptionKey = () => {
    return getCsrf();
  };

  return {
    constants,

    clearAuthentication,
    saveAuthentication,

    getAccessToken,
    saveAccessToken,

    getRefreshToken,

    getSubDomainInSession,
    saveSubDomainInSession,

    hasPendingClientSwitchConfirmation,
    setPendingClientSwitchConfirmation,

    getIsWorkdayUser,
    setIsWorkdayUser,
    deleteIsWorkdayUser,

    getCsrf,
    setCsrf,

    getZendeskUrl,
    getZendeskExpire,
    setZendeskUrl,
    removeZendeskFlag,

    getAegisOAuthProxyVars,
    getAegisOAuthProxyExpire,
    setAegisOAuthProxyVars,
    removeAegisOAuthProxy,

    getRolodexOAuthBounceUrl,
    getRolodexOAuthBounceExpire,
    setRolodexOAuthBounceUrl,
    removeRolodexOAuthBounce,

    getAccessTokenCreationTime,
    saveJwtTokenCreationTime,

    isAegisAuthed,
    isRolodexAuthed,
    isEmbed,
    isLoggedIn,
    isAuthSessionExpired,
    isInactiveTimeoutReached,
    requiresReauth,
    markRequiresReauth,

    removeInactiveTimer,
    resetInactiveTimer,

    getInactiveTimeoutInSeconds,
    getCacheEncryptionKey,
  };
};

export default AuthenticationStore;
